import { useState, useCallback, useMemo, useContext, useRef } from "react";

import { useNavigate, Link } from "react-router-dom";
import { AppBar, Menu, MenuItem, Box, Typography, Drawer } from "@mui/material";
import { usePageHeader } from "../context/PageHeaderContext";
import MenuIcon from "@mui/icons-material/Menu";
import bellIcon from "../assets/bell.svg";
import bellActiveIcon from "../assets/bell-active.svg";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import GridViewIcon from "@mui/icons-material/GridView";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import Logo from "../assets/logo_with_text.png";
import LogoNoText from "../assets/logo_no_text.png";
import { RootContext } from "../context/root-provider";
import NotificationTray from "../components/NotificationTray";
import s from "./navbar.module.scss";

const NavBar = ({ authAttrs, logout, mobileState, xsWindow }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const notificationButtonRef = useRef();

  // Inside a Zoom meeting (TT-1207, F1) the menu is slim: the library in
  // place of Settings, which is for the web app.
  const { displayNotifs, theme, toggleTheme, inMeeting } = useContext(RootContext);
  const { headerContent } = usePageHeader();

  const navigate = useNavigate();

  const handleLogOut = useCallback(
    async (event) => {
      await event.preventDefault();
      setMenuAnchorEl(null);
      setNotificationsAnchorEl(null);
      logout();
    },
    [logout]
  );

  const handleMenuClick = useCallback((event) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleNotificationsClick = useCallback((event) => {
    setNotificationsAnchorEl(event.currentTarget);
  }, []);

  const handleNotificationsClose = useCallback(() => {
    setNotificationsAnchorEl(null);
  }, []);

  const handleNavigation = useCallback(
    (url) => {
      navigate(url);
      setMenuAnchorEl(null);
      setMobileMenuIsOpen(false);
    },
    [navigate]
  );

  const externalLink = useCallback((url) => {
    openLink(url);
    setMenuAnchorEl(null);
    setMobileMenuIsOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuIsOpen((m) => !m);
  }, []);

  const initialsIcon = useMemo(
    () => (
      <>
        <div className={s.circleContainer}>
          <PanoramaFishEyeIcon className={menuAnchorEl ? s.circleSelected : s.circle} />
        </div>
        <div className={s.nameButton}>
          {Array.from(authAttrs.given_name)[0]}
          {Array.from(authAttrs.family_name)[0]}
        </div>
      </>
    ),
    [authAttrs?.family_name, authAttrs?.given_name, menuAnchorEl]
  );

  const notificationButton = useMemo(
    () => (
      <>
        <Box className={s.notificationBtn} ref={notificationButtonRef} onClick={handleNotificationsClick}>
          <img
            src={displayNotifs.length ? bellActiveIcon : bellIcon}
            className={s.bellIcon}
            alt="Notifications"
          />
        </Box>
        <Menu
          id="notifications-menu"
          anchorEl={notificationsAnchorEl}
          keepMounted
          open={!!notificationsAnchorEl}
          onClose={handleNotificationsClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          className={s.dropDownMenu}>
          <NotificationTray close={handleNotificationsClose} />
        </Menu>
      </>
    ),
    [handleNotificationsClick, displayNotifs, notificationsAnchorEl, handleNotificationsClose]
  );

  const menuButton = useMemo(
    () => (
      <>
        <div onClick={handleMenuClick} id="menu_button" className={s.menuButton}>
          {initialsIcon}
        </div>
        <Menu
          id="basic-menu"
          anchorEl={menuAnchorEl}
          keepMounted
          open={!!menuAnchorEl}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          className={s.dropDownMenu}>
          {inMeeting ? (
            <MenuItem onClick={() => handleNavigation("/library")}>Your tapestries</MenuItem>
          ) : (
            <MenuItem onClick={() => handleNavigation("/settings")}>Settings</MenuItem>
          )}
          <MenuItem onClick={() => externalLink("https://support.vtapestry.com/")}>Help</MenuItem>
          <MenuItem onClick={handleLogOut}>Logout</MenuItem>
        </Menu>
      </>
    ),
    [handleMenuClick, initialsIcon, menuAnchorEl, handleMenuClose, handleLogOut, handleNavigation, externalLink, inMeeting]
  );

  const logoSection = useMemo(
    () => (
      <div className={s.navLeft}>
        {xsWindow ? (
          <div className={s.logoContainer}>
            <Link to="/">
              <img
                src={LogoNoText}
                className={s.logoSmall}
                alt="tapestry ai logo"
              />
            </Link>
          </div>
        ) : (
          <div className={s.logoContainer}>
            <Link to="/">
              <img
                src={Logo}
                className={s.logo}
                alt="tapestry ai logo with text"
              />
            </Link>
          </div>
        )}
        {window.location.href.indexOf("dev.tapestryai.com") > -1 && (
          <Typography className={s.devIndicator}>dev</Typography>
        )}
      </div>
    ),
    [xsWindow]
  );

  const mobileMenu = useMemo(
    () => (
      <Box role="presentation" className={s.mobileMenuBox}>
        <div className={s.mobileMenuHeader}>
          {logoSection}
          <CloseIcon onClick={toggleMobileMenu} className={s.closeButton} />
        </div>
        <div className={s.mobileMenuUser}>
          <div className={s.mobileMenuAvatar}>
            {Array.from(authAttrs.given_name)[0]}
            {Array.from(authAttrs.family_name)[0]}
          </div>
          <span className={s.mobileMenuName}>
            {authAttrs?.given_name} {authAttrs?.family_name}
          </span>
        </div>
        <div className={s.mobileMenuDivider} />
        <nav className={s.mobileMenuNav}>
          {inMeeting ? (
            <button className={s.mobileMenuItem} onClick={() => handleNavigation("/library")}>
              <GridViewIcon /> Your tapestries
            </button>
          ) : (
            <button className={s.mobileMenuItem} onClick={() => handleNavigation("/settings")}>
              <SettingsIcon /> Settings
            </button>
          )}
          <button className={s.mobileMenuItem} onClick={() => externalLink("https://support.vtapestry.com/")}>
            <HelpOutlineIcon /> Help
          </button>
          <button className={s.mobileMenuItem} onClick={toggleTheme}>
            {theme === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </nav>
        <div className={s.mobileMenuDivider} />
        <nav className={s.mobileMenuNav}>
          <button className={s.mobileMenuItemDanger} onClick={handleLogOut}>
            <LogoutIcon /> Logout
          </button>
        </nav>
      </Box>
    ),
    [logoSection, toggleMobileMenu, authAttrs, handleLogOut, handleNavigation, externalLink, theme, toggleTheme, inMeeting]
  );

  const mobileMenuButton = useMemo(
    () => (
      <Box className={s.mobileMenuIcon}>
        <MenuIcon
          id="fade-button"
          aria-controls={mobileMenuIsOpen ? "fade-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={mobileMenuIsOpen ? "true" : undefined}
          onClick={toggleMobileMenu}
        />
        <Drawer
          anchor="top"
          open={mobileMenuIsOpen}
          onClose={toggleMobileMenu}
          PaperProps={{ className: s.mobileMenuDrawer }}
          variant="persistent">
          {mobileMenu}
        </Drawer>
      </Box>
    ),
    [mobileMenu, mobileMenuIsOpen, toggleMobileMenu]
  );

  const centerSection = useMemo(
    () => !mobileState && headerContent ? (
      <div className={s.navCenter}>
        {headerContent}
      </div>
    ) : (
      <div className={s.navSpacer} />
    ),
    [mobileState, headerContent]
  );

  const mobileHeaderBar = useMemo(
    () => mobileState && headerContent ? (
      <div className={s.mobileHeaderBar}>
        {headerContent}
      </div>
    ) : null,
    [mobileState, headerContent]
  );

  const rightSection = useMemo(
    () => (
      <div className={s.navRight}>
        <Box
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          className={s.themeToggle}>
          {theme === "dark" ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
        </Box>
        {notificationButton}
        {mobileState ? mobileMenuButton : menuButton}
      </div>
    ),
    [notificationButton, mobileState, mobileMenuButton, menuButton, theme, toggleTheme]
  );

  const res = useMemo(
    () => (
      <Box className={mobileHeaderBar ? s.outerBoxWithSub : s.outerBox}>
        <AppBar elevation={0} className={mobileHeaderBar ? s.appBarWithSub : s.appBar}>
          <div className={s.appBarMain}>
            {logoSection}
            {centerSection}
            {rightSection}
          </div>
          {mobileHeaderBar}
        </AppBar>
      </Box>
    ),
    [logoSection, centerSection, rightSection, mobileHeaderBar]
  );

  return res;
};

function openUrlFromZoomClient(url) {
  let zoomSdkAlias = window.zoomSdk;

  zoomSdkAlias
    .openUrl({
      url: url,
    })
    .then((result) => {
      console.log("openUrlFromZoomClient called for:", url);
      console.log("openUrlFromZoomClient:result:", result);
    });
}

function openLink(url) {
  if (window.zoomSdk) {
    openUrlFromZoomClient(url);
  } else {
    window.open(url, "_blank");
  }
}

export default NavBar;
